/**
 * @boli/core - Message Service
 *
 * Platform-agnostic messaging service for buyer-seller communication.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Per-order message threads
 * - Real-time message delivery
 * - Unread count tracking
 * - Thread management
 *
 * Flow:
 * 1. Order created → thread auto-created for buyer-seller
 * 2. Either party can send messages
 * 3. Messages marked as read when viewed
 * 4. Unread count shown in UI
 */

import type {
  MessageThread,
  Message,
  ThreadListResponse,
  MessageListResponse,
  ApiResponse,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IMessageService {
  /**
   * Get all message threads for current user
   */
  getThreads(): Promise<ThreadListResponse>;

  /**
   * Get or create a thread for an order
   */
  getOrCreateThread(orderId: string): Promise<MessageThread | null>;

  /**
   * Get messages for a thread
   */
  getMessages(threadId: string, page?: number, limit?: number): Promise<MessageListResponse>;

  /**
   * Send a message
   */
  sendMessage(threadId: string, content: string): Promise<ApiResponse<Message>>;

  /**
   * Mark thread as read
   */
  markThreadAsRead(threadId: string): Promise<void>;

  /**
   * Get total unread count
   */
  getUnreadCount(): Promise<number>;
}

export interface MessageServiceConfig {
  apiBaseUrl: string;
  maxMessageLength: number;
  messagesPerPage: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_MESSAGE_CONFIG: MessageServiceConfig = {
  apiBaseUrl: '',
  maxMessageLength: 1000, // Characters
  messagesPerPage: 50,
};

// ============================================================================
// MESSAGE SERVICE IMPLEMENTATION
// ============================================================================

export class MessageService implements IMessageService {
  private config: MessageServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<MessageServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_MESSAGE_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authorization headers
   */
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get all message threads for current user
   */
  async getThreads(): Promise<ThreadListResponse> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/messages/threads`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch threads');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get threads:', error);
      return { threads: [], total: 0 };
    }
  }

  /**
   * Get or create a thread for an order
   */
  async getOrCreateThread(orderId: string): Promise<MessageThread | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/messages/threads/${orderId}`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to get/create thread');
      }

      const data = await response.json();
      return data.thread;
    } catch (error) {
      console.error('Failed to get/create thread:', error);
      return null;
    }
  }

  /**
   * Get messages for a thread
   */
  async getMessages(
    threadId: string,
    page: number = 0,
    limit: number = this.config.messagesPerPage
  ): Promise<MessageListResponse> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/messages/threads/${threadId}/messages?${params}`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      return {
        messages: data.messages || [],
        total: data.total || 0,
        hasMore: (data.messages?.length || 0) === limit,
      };
    } catch (error) {
      console.error('Failed to get messages:', error);
      return { messages: [], total: 0, hasMore: false };
    }
  }

  /**
   * Send a message
   */
  async sendMessage(threadId: string, content: string): Promise<ApiResponse<Message>> {
    try {
      // Validate content
      const trimmed = content.trim();
      if (!trimmed) {
        return {
          success: false,
          error: {
            code: 'EMPTY_MESSAGE',
            message: 'Message cannot be empty',
          },
        };
      }

      if (trimmed.length > this.config.maxMessageLength) {
        return {
          success: false,
          error: {
            code: 'MESSAGE_TOO_LONG',
            message: `Message cannot exceed ${this.config.maxMessageLength} characters`,
          },
        };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/messages/threads/${threadId}/messages`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
          body: JSON.stringify({ content: trimmed }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.code || 'SEND_FAILED',
            message: data.message || 'Failed to send message',
          },
        };
      }

      return { success: true, data: data.message };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Mark thread as read
   */
  async markThreadAsRead(threadId: string): Promise<void> {
    try {
      await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/messages/threads/${threadId}/read`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
        }
      );
    } catch (error) {
      console.error('Failed to mark thread as read:', error);
    }
  }

  /**
   * Get total unread count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/messages/unread-count`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch unread count');
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      console.error('Failed to get unread count:', error);
      return 0;
    }
  }

  /**
   * Format timestamp for display
   */
  formatMessageTime(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      // Today: show time
      return date.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      // This week: show day name
      return date.toLocaleDateString('en-PK', { weekday: 'short' });
    } else {
      // Older: show date
      return date.toLocaleDateString('en-PK', { month: 'short', day: 'numeric' });
    }
  }

  /**
   * Group messages by date for UI
   */
  groupMessagesByDate(messages: Message[]): Map<string, Message[]> {
    const groups = new Map<string, Message[]>();

    for (const message of messages) {
      const date = new Date(message.createdAt);
      const dateKey = date.toLocaleDateString('en-PK', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const existing = groups.get(dateKey) || [];
      existing.push(message);
      groups.set(dateKey, existing);
    }

    return groups;
  }

  /**
   * Check if message is from current user
   */
  isOwnMessage(message: Message, currentUserId: string): boolean {
    return message.senderId === currentUserId;
  }
}

// ============================================================================
// MESSAGE SERVICE FACTORY
// ============================================================================

let messageServiceInstance: MessageService | null = null;

export function createMessageService(
  config: Partial<MessageServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): MessageService {
  messageServiceInstance = new MessageService(config, fetchAdapter, getAuthToken);
  return messageServiceInstance;
}

export function getMessageService(): MessageService {
  if (!messageServiceInstance) {
    throw new Error('MessageService not initialized. Call createMessageService first.');
  }
  return messageServiceInstance;
}
